import { useEffect, useState } from "react";

import { wrapFetchForAuth } from "./lib/authFetch";
import { wsClient } from "./ws";

export type SessionStatus = "unknown" | "in" | "out";

/**
 * Whether this browser holds a session, as a module-level singleton with a
 * subscribe hook — the same shape as wsClient, and for the same reason: the
 * state is per-tab rather than per-component, and both the app root and the
 * Manage page need to read and change it without threading a context through
 * every screen between them.
 *
 * The cookie itself is HttpOnly, so "are we logged in" can only ever be the
 * server's answer, never something read off document.cookie.
 */
class SessionStore {
	public status: SessionStatus = "unknown";
	private listeners = new Set<(s: SessionStatus) => void>();
	private started = false;

	start() {
		if (this.started) return;
		this.started = true;

		// Installed before the first request so an expired cookie is caught
		// wherever it surfaces, not only on the session check below.
		const guarded = wrapFetchForAuth({
			baseFetch: window.fetch.bind(window),
			origin: location.origin,
			onUnauthorized: () => this.setStatus("out"),
		});
		// window.fetch carries extra members beyond the call signature
		// (fetch.preconnect); copy whatever the platform put there rather than
		// replacing it with a bare function.
		window.fetch = Object.assign(guarded, window.fetch);

		void this.refresh();
	}

	async refresh() {
		try {
			const res = await fetch("/api/auth/session");
			const data = (await res.json()) as { authed: boolean };
			this.setStatus(data.authed ? "in" : "out");
		} catch {
			// Server unreachable is indistinguishable from logged out from here,
			// and showing the login screen is the recoverable of the two.
			this.setStatus("out");
		}
	}

	/** Resolves to an error message, or null when the password was accepted. */
	async login(password: string): Promise<string | null> {
		let res: Response;
		try {
			res = await fetch("/api/auth/login", {
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ password }),
			});
		} catch {
			return "Could not reach the server.";
		}

		if (res.ok) {
			this.setStatus("in");
			return null;
		}
		if (res.status === 429) {
			const data = (await res.json().catch(() => ({}))) as {
				retryAfterMs?: number;
			};
			const mins = Math.ceil((data.retryAfterMs ?? 0) / 60_000);
			return `Too many attempts. Try again in ${mins} minute${mins === 1 ? "" : "s"}.`;
		}
		return "Wrong password.";
	}

	async logout() {
		try {
			await fetch("/api/auth/logout", { method: "POST" });
		} finally {
			this.setStatus("out");
		}
	}

	private setStatus(next: SessionStatus) {
		if (this.status === next) return;
		this.status = next;
		// The socket was authorised at upgrade time, so it would otherwise
		// outlive the session that opened it — and reconnect forever against a
		// 401 once the cookie is gone.
		if (next === "out") wsClient.disconnect();
		for (const l of this.listeners) l(next);
	}

	subscribe(l: (s: SessionStatus) => void): () => void {
		this.listeners.add(l);
		return () => this.listeners.delete(l);
	}
}

export const session = new SessionStore();

export function useSession(): SessionStatus {
	const [status, setStatus] = useState(session.status);
	useEffect(() => {
		session.start();
		setStatus(session.status);
		return session.subscribe(setStatus);
	}, []);
	return status;
}
